import { Injectable } from '@angular/core';
import { LocalStorageService } from '../../services/local-storage.service';
import { ApiClientHelper } from '../../helpers/api-client.helper';
import { BehaviorSubject } from 'rxjs';
import { UserPinModel } from '../../../shared/models/user-pin.model';
import { UserAlias } from '../../../shared/constants';
import { StorageKeys } from '../../../shared/models/storage-keys';
import { AppConfig } from '../../../shared/config/app-config';
import { ModalOptionsConfig, ModalService } from '../../../lean-shared/components/nz-modal-service/modal.service';
import { UserPinSetupModalComponent } from '../../../lean-shared/components/user-pin-setup-modal/user-pin-setup-modal.component';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { UserAuthorityService } from '../../services/user-authority.service';
import { StringHelper } from '../../helpers/strings.helper';

@Injectable({
    providedIn: 'root',
})
export class UserPinService {
    userPinDetailsSubject: BehaviorSubject<UserPinModel>;
    runningModal: NzModalRef;

    constructor(
        private localStorageService: LocalStorageService,
        private apiClientHelper: ApiClientHelper,
        private appConfig: AppConfig,
        private modalService: ModalService,
        private userAuthService: UserAuthorityService,
    ) {
        this.userPinDetailsSubject = new BehaviorSubject<UserPinModel>(this.getUserPinDetails());
    }

    getUserPinDetails(): UserPinModel {
        return {
            hasBusinessPin: this.localStorageService.get('hasBusinessPin') === 'true',
            totalBusinesses: +(this.localStorageService.get(StorageKeys.TOTAL_BUSINESSES) || 0),
            hasUserPin: this.localStorageService.get('hasUserPin') === 'true',
            userIsBo: this.localStorageService.get('selected_role') === UserAlias.agentManager,
            isBusinessPinBlocked: this.localStorageService.get('isBusinessPinBlocked') === 'true',
            isBusinessOwnerPinBlocked: this.localStorageService.get('isBusinessOwnerPinBlocked') === 'true',
            performFaceMatchCheckOnPasswordChange: this.localStorageService.get(StorageKeys.PERFORM_FACE_MATCH_CHECK_ON_PASSWORD_CHANGE) === 'true',
            performFaceMatchCheckOnUserPinReset: this.localStorageService.get(StorageKeys.PERFORM_FACE_MATCH_CHECK_ON_USER_PIN_RESET) === 'true',
        };
    }

    updateUserPinDetailsSubject() {
        this.userPinDetailsSubject.next(this.getUserPinDetails());
    }

    showSetBusinessPinModal(): boolean {
        return !this.userPinDetailsSubject.value?.hasUserPin && this.appConfig.SHOW_USER_PIN_MODAL_ON_BO_LOGIN;
    }

    showUserPinSetUpModal(dismissible = true) {
        if (!this.showSetBusinessPinModal()) {
            return;
        }

        try {
            const config = new ModalOptionsConfig();
            config.nzComponentParams.data = {
                dismissible,
            };
            config.nzMaskClosable = dismissible;
            config.nzComponentParams.callback = (comment) => {};
            config.nzComponentParams.cancelName = () => {
                if (!config.nzComponentParams.data.dismissible) {
                    this.createModal(config);
                }
            };
            this.createModal(config);
        } catch (e) {
            console.log(e);
        }
    }

    createModal(config) {
        this.runningModal = this.modalService.createModal(UserPinSetupModalComponent, config);
    }

    shouldFetchPinCheckInfo() {
        return this.userAuthService.hasAuthority('mab_fo_su_must_have_user_pin');
    }

    getBusinessUserPinDetails(dismissible?: boolean) {
        if (!this.shouldFetchPinCheckInfo()) {
            return;
        }
        let agentId;
        this.updateUserPinDetailsSubject();
        if (this.userPinDetailsSubject.value?.userIsBo) {
            const dbInfo = JSON.parse(this.localStorageService.get('agentDashboardData') || '{}');
            const agentManagerId = dbInfo?.agentManagerId ? +dbInfo?.agentManagerId : undefined;
            agentId = { agentManagerId };
            if (!agentManagerId) {
                this.getBusinessUserPinDetails(dismissible);
            }
        } else {
            const moniepointAgentId = this.localStorageService.get(StorageKeys.moniepointAgentId);
            agentId = moniepointAgentId ? { agentId: +moniepointAgentId } : {};
        }

        const url = `${this.appConfig.MONIEPOINT_USER_URL}/api/v1/user-management/pin-check-info`;
        return this.apiClientHelper.postForObservable(url, { ...agentId }).subscribe((res) => {
            this.updateUserPinDetails(res);
            this.showUserPinSetUpModal(dismissible);
        });
    }

    updateUserPinDetails(res: any) {
        this.localStorageService.save('hasBusinessPin', res?.hasBusinessPin);
        this.localStorageService.save('isBusinessPinBlocked', res?.isBusinessPinBlocked);
        this.localStorageService.save('isBusinessOwnerPinBlocked', res?.isBusinessOwnerPinBlocked);
        this.localStorageService.save(StorageKeys.TOTAL_BUSINESSES, res?.businessCount || 0);
        this.localStorageService.save('hasUserPin', res?.hasUserPin);
        this.localStorageService.save(StorageKeys.PERFORM_FACE_MATCH_CHECK_ON_PASSWORD_CHANGE, res?.performFaceMatchCheckOnPasswordChange);
        this.localStorageService.save(StorageKeys.PERFORM_FACE_MATCH_CHECK_ON_USER_PIN_RESET, res?.performFaceMatchCheckOnUserPinReset);
        this.userPinDetailsSubject.next(this.getUserPinDetails());
    }

    viewPartnerValidation(data?): Promise<any> {
        const url = `${this.appConfig.MONIEPOINT_HOST}/api/v1/partners/view-earnings-validation`;
        return this.apiClientHelper.postForPromise(url, data);
    }

    enquiryPartnerValidation(params): Promise<any> {
        params = StringHelper.toUrlString(params);
        const url = `${this.appConfig.MONIEPOINT_HOST}/api/v1/partners/view-earnings-validity-enquiry?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }
}
